import { Button } from "@/components/ui/button";
import { ScreenSpinner } from "@/components/ui/spinner";
import { request } from "@/lib/request";
import { User } from "@/lib/types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { TriangleAlertIcon } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const searchSchema = z.object({
  token: z.string().nonempty().catch(""),
});

export const Route = createFileRoute("/connect")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const accessToken = localStorage.getItem("unideck.auth.accessToken");

    return { accessToken };
  },
  component: memo(RouteComponent),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const { accessToken } = Route.useLoaderData();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setStorage = useCallback(
    (data: { accessToken: string; user: User }) => {
      localStorage.setItem("unideck.auth.accessToken", data.accessToken);
    },
    [],
  );

  const clearLoginModalState = async () => {
    return localStorage.setItem("unideck.auth.dismissed", "true");
  };

  const fetchUser = async (accessToken: string) => {
    const res = await request
      .get(`/user/info`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .catch(err => {
        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to load user profile",
        );
      });

    return res.data.data;
  };

  const updateUser = useCallback(
    async (accessToken: string, retryCount: number = 0) => {
      try {
        const data = await fetchUser(accessToken);
        setStorage({ accessToken, user: data });

        captureEvent("onboarding_connect_succeeded", {
          service: "account_link",
          flow: "callback",
        });

        await clearLoginModalState();
        navigate({
          to: "/",
          search: {
            change_password_token: undefined,
            reset_password_token: undefined,
          },
        });
      } catch {
        if (retryCount < 3) {
          updateUser(accessToken, retryCount + 1);
        } else if (retryCount < 5) {
          setTimeout(() => {
            updateUser(accessToken, retryCount + 1);
          }, retryCount * 1000);
        } else {
          captureEvent("onboarding_connect_failed", {
            service: "account_link",
            flow: "callback",
            reason: "Failed to load user profile",
          });
          setError("Failed to load user profile");
        }
      }
    },
    [setStorage, navigate],
  );

  const handleToken = useCallback(async () => {
    setLoading(true);
    captureEvent("onboarding_connect_started", {
      service: "account_link",
      flow: "callback",
    });

    if (!token) {
      navigate({
        to: "/",
        search: {
          change_password_token: undefined,
          reset_password_token: undefined,
        },
      });
      return;
    }

    if (!accessToken) {
      await updateUser(token);
      return;
    }

    try {
      const res = await request.post(
        `/auth/link`,
        { token },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      await updateUser(res.data.data.access_token);
    } catch {
      captureEvent("onboarding_connect_failed", {
        service: "account_link",
        flow: "callback",
        reason: sanitizeErrorReason("Link request failed"),
      });
      await updateUser(token);
    }
  }, [token, accessToken, navigate, updateUser]);

  useEffect(() => {
    handleToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-background">
      {loading ? (
        <div className="flex flex-col gap-4">
          <ScreenSpinner />
          <span className="animate-pulse text-muted-foreground">
            Fetching account information
          </span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-4">
          <TriangleAlertIcon className="size-16 text-white" />
          <span className="text-white">
            {error || "Something went wrong, please try logging in again"}
          </span>
          <Button variant="default" asChild>
            <Link
              to="/"
              search={{
                change_password_token: undefined,
                reset_password_token: undefined,
              }}
            >
              Go back
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
